import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, FileTextIcon, MessageCircleIcon, DatabaseIcon, HomeIcon, UsersIcon, DropletIcon, UserIcon, CreditCardIcon, ShieldCheckIcon, TrendingUpIcon, StarIcon, SettingsIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
import { atnaFinalCtaCopy as c, atnaServices, atnaFoundation, atnaOutcomes, ATNA_OFFICIAL_LOGO, type Bi, type ServiceKey, type FoundationKey, type OutcomeKey } from '../../data/atnaFinalCta';
type Pick = (b: Bi) => string;
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const SERVICE_ICONS: Record<ServiceKey, typeof FileTextIcon> = {
  requests: FileTextIcon,
  comms: MessageCircleIcon,
  collect: DatabaseIcon,
  permits: HomeIcon,
  leisure: UsersIcon,
  meters: DropletIcon
};
const FOUNDATION_ICONS: Record<FoundationKey, typeof FileTextIcon> = {
  identity: UserIcon,
  card: CreditCardIcon,
  consent: ShieldCheckIcon
};
const OUTCOME_ICONS: Record<OutcomeKey, typeof FileTextIcon> = {
  capacity: TrendingUpIcon,
  quality: StarIcon,
  teams: ShieldCheckIcon,
  integrated: SettingsIcon
};

/* Connector stub drawn in the gap between a service card and the ATNA core. */
function Stub({
  side,
  pos
}: {
  side: 'left' | 'right';
  pos: 0 | 1 | 2;
}) {
  const d = pos === 1 ? 'M0 40 L56 40' : pos === 0 ? 'M0 40 C 26 40, 26 78, 56 80' : 'M0 40 C 26 40, 26 2, 56 0';
  const end = pos === 1 ? 40 : pos === 0 ? 80 : 0;
  return <svg className={`hidden xl:block absolute top-1/2 -translate-y-1/2 w-14 h-20 overflow-visible ${side === 'left' ? 'left-full' : 'right-full -scale-x-100'}`} viewBox="0 0 56 80" aria-hidden="true" data-id="element-799" data-mp="2kjbdvw2ovmzn">
      <path d={d} fill="none" stroke="#9F9AF2" strokeWidth="1.4" data-id="element-800" data-mp="2hi8au71bkvhu" />
      <circle cx="0" cy="40" r="2.6" fill="#6D66F0" data-id="element-801" data-mp="2dgd8r0bkee30" />
      <circle cx="56" cy={end} r="2.6" fill="#6D66F0" data-id="element-802" data-mp="2dhobqxx6rht2" />
    </svg>;
}
function ServiceCard({
  s,
  pos,
  pick
}: {
  s: (typeof atnaServices)[number];
  pos: 0 | 1 | 2;
  pick: Pick;
}) {
  const Icon = SERVICE_ICONS[s.key];
  return <div className="relative" data-id="element-803" data-mp="30gb2yup5z8jv">
      <div className="rounded-[18px] bg-white border border-[#EEEDF7] shadow-[0_14px_32px_-26px_rgba(18,26,58,0.3)] px-3.5 py-3 flex items-center gap-3" data-id="element-804" data-mp="30fj7djvkhatl">
        <span className="w-10 h-10 rounded-full bg-[#F3F2FD] flex items-center justify-center shrink-0" data-id="element-805" data-mp="2wcwmk2xbcq5w">
          <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-806" data-mp="357194a5ezffm" />
        </span>
        <span className="min-w-0" data-id="element-807" data-mp="3k4bj1f2esd98">
          <span className="block font-sans text-[14.5px] font-semibold text-[#121A3A]" data-id="element-808" data-mp="3k5d6yk4iwu8d">{pick(s.title)}</span>
          <span className="block font-sans text-[12.5px] text-[#5A6180] mt-0.5 whitespace-nowrap" data-id="element-809" data-mp="3f6icgkx71h8f">{pick(s.desc)}</span>
        </span>
      </div>
      <Stub side={s.side} pos={pos} data-id="element-810" data-mp="2js6bs525vwa" />
    </div>;
}
function AtnaCore({
  pick
}: {
  pick: Pick;
}) {
  return <div className="relative w-[210px] h-[210px] md:w-[230px] md:h-[230px] shrink-0 mx-auto" data-id="element-811" data-mp="3ostajnkcnipr">
      <div className="absolute inset-[-56px] rounded-full border border-dashed border-[#DEDCF7]" aria-hidden="true" data-id="element-812" data-mp="3trxjcd3d3efk" />
      <div className="absolute inset-[-30px] rounded-full border border-[#E7E5FA]" aria-hidden="true" data-id="element-813" data-mp="3f2hvb4v7tn3k" />
      <div className="absolute inset-[-10px] rounded-full bg-[#E4E2FB] opacity-70 blur-2xl" aria-hidden="true" data-id="element-814" data-mp="3ju9m9gia9uu8" />
      <div className="relative w-full h-full rounded-full bg-[#FAFAFF] border-[6px] border-white shadow-[0_24px_60px_-24px_rgba(79,70,229,0.4),inset_0_0_40px_rgba(228,226,251,0.9)] flex flex-col items-center justify-center gap-4" data-id="element-815" data-mp="363ee66flzhrp">
        <img src={ATNA_OFFICIAL_LOGO} alt="ATNA" className="w-[146px] md:w-[160px] h-auto object-contain mix-blend-multiply -my-7" data-id="element-816" data-mp="2w4xgyrq2u6x7" />
        <p className="font-sans text-[11px] md:text-[12px] font-medium tracking-[0.3em] text-[#5A6180] text-center leading-[1.7]" data-id="element-817" data-mp="87qjdc4j6hs1">
          {c.coreLabel.map(l => <span key={l.fr} className="block" data-id="element-818" data-mp="3uo304bx35v0p">{pick(l)}</span>)}
        </p>
      </div>
    </div>;
}
function Foundation({
  pick
}: {
  pick: Pick;
}) {
  return <div className="flex flex-col items-center" data-id="element-819" data-mp="3pnfd01bz9ybr">
      <span className="flex flex-col items-center" aria-hidden="true" data-id="element-820" data-mp="3kr84c0s768q4">
        <span className="w-2 h-2 rounded-full bg-[#6D66F0]" data-id="element-821" data-mp="3gotlvhsfxazd" />
        <span className="w-px h-10 bg-[#9F9AF2]" data-id="element-822" data-mp="3gna8byy65e1a" />
        <span className="w-2 h-2 rounded-full bg-[#6D66F0]" data-id="element-823" data-mp="3bop4lpjpkc83" />
      </span>
      <div className="mt-1 w-full max-w-[480px] rounded-[34px] bg-[#FBFBFF] border border-white shadow-[0_18px_44px_-26px_rgba(79,70,229,0.35)] ring-1 ring-[#ECEBF8] px-4 py-3.5" aria-label={pick(c.foundationLabel)} role="group" data-id="element-824" data-mp="36qdwpkcz4bsi">
        <ul className="grid grid-cols-3" data-id="element-825" data-mp="14zkdmn3s1cfr">
          {atnaFoundation.map((f, i) => {
          const Icon = FOUNDATION_ICONS[f.key];
          return <li key={f.key} className={`flex flex-col items-center gap-2 px-2 text-center ${i > 0 ? 'border-l border-[#E3E2F2]' : ''}`} data-id="element-826" data-mp="1hh4wgpyo1jmi" data-mpi={i}>
                <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.5} aria-hidden="true" data-id="element-827" data-mp="1r76i7b3jqpy9" />
                <span className="font-sans text-[12.5px] md:text-[13px] font-medium text-[#121A3A] leading-tight" data-id="element-828" data-mp="tjxs12slospx">{pick(f.label)}</span>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}
export function AtnaFinalCTA() {
  const {
    lang
  } = useLanguage();
  const pick: Pick = b => lang === 'FR' ? b.fr : b.en;
  const left = atnaServices.filter(s => s.side === 'left');
  const right = atnaServices.filter(s => s.side === 'right');
  return <section className="relative overflow-hidden bg-[#FDFDFF] py-24 lg:py-28 px-6" aria-labelledby="atna-final-title" data-id="element-829" data-mp="2ezd8ptmlc2pr">
      <div className="pointer-events-none absolute -top-20 right-[8%] w-[820px] h-[620px] rounded-full bg-[#EEEDFD] opacity-70 blur-[150px]" aria-hidden="true" data-id="element-830" data-mp="jtlzkurv7edv" />

      <div className="relative max-w-[1520px] mx-auto" data-id="element-831" data-mp="euj7sc0wb4nu">
        <div className="grid grid-cols-1 xl:grid-cols-[42fr_58fr] gap-14 xl:gap-6 items-center" data-id="element-832" data-mp="sutztidxypal">
          {/* LEFT: message + single CTA */}
          <motion.div {...reveal} className="min-w-0" data-id="element-833" data-mp="otql4z7mqzay">
            <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-834" data-mp="12l5ol5lq916">
              {pick(c.eyebrow)}
            </p>
            <h2 id="atna-final-title" className="font-serif text-[2.2rem] sm:text-[2.8rem] lg:text-[3.1rem] xl:text-[2.6rem] 2xl:text-[3rem] leading-[1.02] tracking-tight mt-5" data-id="element-835" data-mp="9voegg5axxph">
              {c.headline.map(l => <span key={l.text.fr} className={`block xl:whitespace-nowrap ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-836" data-mp="3dk9i1oz3h5om">
                  {pick(l.text)}
                </span>)}
            </h2>
            <p className="font-sans text-[15.5px] md:text-[16.5px] text-[#4A5268] leading-relaxed mt-6 max-w-[560px]" data-id="element-837" data-mp="2ze7vjrzoc6j2">
              {c.body.map(l => <span key={l.fr} className="xl:block" data-id="element-838" data-mp="2ph38zr1x291q">{pick(l)} </span>)}
            </p>
            <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
            location: 'ATNA Final CTA'
          })} className="mt-8 inline-flex items-center justify-center gap-3 rounded-xl bg-[#4F46E5] px-10 py-3.5 font-sans text-[16px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2" data-id="element-839" data-mp="2ug63ihk9swvg">
              {pick(c.cta)}
              <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-840" data-mp="1wrcs5a8wgt1x" />
            </Link>
          </motion.div>

          {/* RIGHT: services ⟷ ATNA, foundation beneath */}
          <motion.div {...reveal} className="min-w-0" data-id="element-841" data-mp="1j182de90kdmf">
            {/* Desktop */}
            <div className="hidden xl:block" data-id="element-842" data-mp="1e24z9zclghh3">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-x-14 items-center max-w-[860px] mx-auto" data-id="element-843" data-mp="1e0ac0eduod9u">
                <ul className="flex flex-col gap-5" data-id="element-844" data-mp="19320gwn7j7hk">
                  {left.map((s, i) => <li key={s.key} data-id="element-845" data-mp="152zy0l9nea18" data-mpi={i}><ServiceCard s={s} pos={i as 0 | 1 | 2} pick={pick} data-id="element-846" data-mp="1539som5n15d9" /></li>)}
                </ul>
                <AtnaCore pick={pick} data-id="element-847" data-mp="1wscsnpqhrtk3" />
                <ul className="flex flex-col gap-5" data-id="element-848" data-mp="1wte9kau6va9q">
                  {right.map((s, i) => <li key={s.key} data-id="element-849" data-mp="1s0l41kqwbxwl" data-mpi={i}><ServiceCard s={s} pos={i as 0 | 1 | 2} pick={pick} data-id="element-850" data-mp="1s0386p85blp7" /></li>)}
                </ul>
              </div>
              <div className="-mt-2 px-[14%]" data-id="element-851" data-mp="2khxecge154x2">
                <Foundation pick={pick} data-id="element-852" data-mp="2kiqpdtk0ugl0" />
              </div>
            </div>

            {/* Tablet & mobile */}
            <div className="xl:hidden flex flex-col gap-12" data-id="element-853" data-mp="2fpnvfkbc154a">
              <div className="pt-16" data-id="element-854" data-mp="294c7gjd1q8d">
                <AtnaCore pick={pick} data-id="element-855" data-mp="2a5o1n87agr5" />
                <div className="pt-12" data-id="element-856" data-mp="77e4njdpacqa">
                  <Foundation pick={pick} data-id="element-857" data-mp="78gtllrgq53k" />
                </div>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-858" data-mp="b8zmlj53dh7c">
                {atnaServices.map(s => <li key={s.key} data-id="element-859" data-mp="g95aco88qjk5"><ServiceCard s={s} pos={1} pick={pick} data-id="element-860" data-mp="g9x2fp2mpz5o" /></li>)}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* BOTTOM: signature + business outcomes */}
        <motion.div {...reveal} className="mt-16 rounded-3xl border border-[#ECEBF5] bg-white/80 overflow-hidden grid grid-cols-1 xl:grid-cols-[25fr_75fr]" data-id="element-861" data-mp="q4p5v9iw7g8d">
          <div className="bg-[#F5F5FC] px-8 py-6 flex items-center" data-id="element-862" data-mp="yybwymc5nvb8">
            <p className="font-serif text-[1.5rem] md:text-[1.7rem] leading-[1.18] tracking-tight" data-id="element-863" data-mp="13ypvndrv9swl">
              {c.signature.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-864" data-mp="18ux1l6k9j4ej">
                  {pick(l.text)}
                </span>)}
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 px-6 md:px-8 py-6 gap-y-6" data-id="element-865" data-mp="2lf9tyjn43r2k">
            {atnaOutcomes.map((o, i) => {
            const Icon = OUTCOME_ICONS[o.key];
            return <li key={o.key} className={`flex items-start gap-3.5 xl:px-5 ${i > 0 ? 'xl:border-l xl:border-[#E6E5F1]' : 'xl:pl-0'}`} data-id="element-866" data-mp="2z9awzpl3k08q" data-mpi={i}>
                  <span className="w-11 h-11 rounded-full bg-[#F3F2FD] flex items-center justify-center shrink-0" data-id="element-867" data-mp="3978k29r7zqzr">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-868" data-mp="3d9bxv51d37o7" />
                  </span>
                  <span className="min-w-0 pt-0.5" data-id="element-869" data-mp="1eu8xfwof9wiz">
                    <span className="block font-sans text-[14px] font-semibold text-[#121A3A] leading-snug" data-id="element-870" data-mp="1evjyhoqhgq6e">
                      {o.title.map(t => <span key={t.fr} className="block" data-id="element-871" data-mp="1bbjl770hp1g2">{pick(t)}</span>)}
                    </span>
                    <span className="block font-sans text-[13px] text-[#5A6180] leading-snug mt-1.5" data-id="element-872" data-mp="16kbam7nfba1b">
                      {o.desc.map(t => <span key={t.fr} className="block" data-id="element-873" data-mp="11m9v1wp7hsul">{pick(t)}</span>)}
                    </span>
                  </span>
                </li>;
          })}
          </ul>
        </motion.div>
      </div>
    </section>;
}